import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { EVENT_PRIORITY_OPTIONS } from '../utils/raceTypes';
import './AddCalendarEventModal.css';

const overlayBaseStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000, // Must be above sidebar (Sidebar.css uses 10001)
};

const modalStyle = {
  backgroundColor: '#0f0f0f',
  color: '#e0e0e0',
  padding: '18px',
  borderRadius: '10px',
  border: '1px solid #2a2a2a',
  width: '92vw',
  maxWidth: '560px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
};

const actionBtnStyle = {
  background: 'transparent',
  border: '1px solid #2a2a2a',
  color: '#e0e0e0',
  borderRadius: '8px',
  padding: '10px 12px',
  cursor: 'pointer',
  fontWeight: 700,
};

const iconBtnStyle = {
  ...actionBtnStyle,
  padding: '8px 10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const primaryBtnStyle = {
  ...actionBtnStyle,
  background: 'var(--brand-accent-gradient)',
  border: 'none',
  color: '#fff',
};

function normalizeDayKey(value) {
  // expects YYYY-MM-DD; fallback to today if invalid
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function AddCalendarEventModal({
  isOpen,
  onClose,
  defaultDayKey,
  busy,
  mode, // 'create' | 'edit'
  initialEvent,
  onSubmit,
  onDelete,
  raceTypeOptions,
  onManageRaceTypes,
}) {
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [dayKey, setDayKey] = useState(() => normalizeDayKey(defaultDayKey));
  const [type, setType] = useState('workout'); // workout | competition
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [raceTypeId, setRaceTypeId] = useState('public:xco');
  const [eventPriority, setEventPriority] = useState('B'); // A | B | C
  const [effortMode, setEffortMode] = useState('percent'); // percent | minutes (from race type, read-only in event)
  const [durationMinutes, setDurationMinutes] = useState('');
  const [zoneZ1, setZoneZ1] = useState('');
  const [zoneZ2, setZoneZ2] = useState('');
  const [zoneZ3, setZoneZ3] = useState('');
  const [zoneZ4, setZoneZ4] = useState('');
  const [zoneZ5, setZoneZ5] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setDayKey(normalizeDayKey(defaultDayKey));
  }, [isOpen, defaultDayKey]);

  useEffect(() => {
    if (!isOpen) return;
    if (!initialEvent) return;

    setDayKey(normalizeDayKey(initialEvent.dayKey));
    const normalizedType = initialEvent.type === 'goal' ? 'competition' : initialEvent.type;
    setType(normalizedType || 'workout');
    setTitle(initialEvent.title || '');
    setNotes(initialEvent.notes || '');

    const comp = initialEvent.competition || null;
    if (comp) {
      const rtId = comp.raceTypeId || 'public:other';
      setRaceTypeId(rtId);
      setEventPriority(comp.priority === 'A' || comp.priority === 'B' || comp.priority === 'C' ? comp.priority : 'B');
      const ep = comp.effortProfile || {};
      setEffortMode(ep.mode === 'minutes' ? 'minutes' : 'percent');
      setDurationMinutes(ep.durationMinutes != null ? String(ep.durationMinutes) : '');
      const zm = ep.zoneMix || {};
      setZoneZ1(zm.z1 != null ? String(zm.z1) : '');
      setZoneZ2(zm.z2 != null ? String(zm.z2) : '');
      setZoneZ3(zm.z3 != null ? String(zm.z3) : '');
      setZoneZ4(zm.z4 != null ? String(zm.z4) : '');
      setZoneZ5(zm.z5 != null ? String(zm.z5) : '');
    }
  }, [initialEvent, isOpen]);

  const selectedRaceType = useMemo(() => {
    const all = [
      ...(raceTypeOptions?.publicList || []),
      ...(raceTypeOptions?.userList || []),
    ];
    return all.find((t) => t.id === raceTypeId) || null;
  }, [raceTypeId, raceTypeOptions]);

  // When race type changes, always apply its defaults (duration and zone mix come from race type only)
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedRaceType?.defaults) {
      setDurationMinutes('');
      setZoneZ1('');
      setZoneZ2('');
      setZoneZ3('');
      setZoneZ4('');
      setZoneZ5('');
      return;
    }
    const d = selectedRaceType.defaults;
    setDurationMinutes(d.durationMinutes != null ? String(d.durationMinutes) : '');
    setEffortMode(d.effortMode === 'minutes' ? 'minutes' : 'percent');
    const zm = d.zoneMix || {};
    setZoneZ1(zm.z1 != null ? String(zm.z1) : '');
    setZoneZ2(zm.z2 != null ? String(zm.z2) : '');
    setZoneZ3(zm.z3 != null ? String(zm.z3) : '');
    setZoneZ4(zm.z4 != null ? String(zm.z4) : '');
    setZoneZ5(zm.z5 != null ? String(zm.z5) : '');
  }, [isOpen, selectedRaceType?.id, selectedRaceType?.defaults]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit') return;
    // Default: if creating an event on a future date, assume it's a competition.
    const dk = normalizeDayKey(defaultDayKey);
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const parts = dk.split('-').map((x) => Number(x));
    if (parts.length === 3 && Number.isFinite(parts[0]) && Number.isFinite(parts[1]) && Number.isFinite(parts[2])) {
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      if (d.getTime() > todayMidnight.getTime()) setType('competition');
    }
  }, [defaultDayKey, isOpen, mode]);

  useEffect(() => {
    if (!isOpen) return;

    const measure = () => {
      const el = document.querySelector('.sidebar');
      const w = el && el.getBoundingClientRect ? el.getBoundingClientRect().width : 0;
      setSidebarWidth(Number.isFinite(w) ? w : 0);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  const overlayStyle = useMemo(() => {
    return {
      ...overlayBaseStyle,
      left: sidebarWidth ? `${sidebarWidth}px` : 0,
      width: sidebarWidth ? `calc(100% - ${sidebarWidth}px)` : '100%',
    };
  }, [sidebarWidth]);

  const effortProfile = useMemo(() => {
    const duration = durationMinutes === '' ? null : Number(durationMinutes);
    const z1 = zoneZ1 === '' ? null : Number(zoneZ1);
    const z2 = zoneZ2 === '' ? null : Number(zoneZ2);
    const z3 = zoneZ3 === '' ? null : Number(zoneZ3);
    const z4 = zoneZ4 === '' ? null : Number(zoneZ4);
    const z5 = zoneZ5 === '' ? null : Number(zoneZ5);

    return {
      mode: effortMode,
      durationMinutes: Number.isFinite(duration) ? duration : null,
      zoneMix: {
        z1: Number.isFinite(z1) ? z1 : null,
        z2: Number.isFinite(z2) ? z2 : null,
        z3: Number.isFinite(z3) ? z3 : null,
        z4: Number.isFinite(z4) ? z4 : null,
        z5: Number.isFinite(z5) ? z5 : null,
      },
    };
  }, [durationMinutes, effortMode, zoneZ1, zoneZ2, zoneZ3, zoneZ4, zoneZ5]);

  if (!isOpen) return null;

  const canSave = !!title.trim() && /^\d{4}-\d{2}-\d{2}$/.test(dayKey);
  const isCompetition = type === 'competition';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>{mode === 'edit' ? 'Edit Event' : 'Add Event'}</div>
          <button
            type="button"
            style={iconBtnStyle}
            onClick={onClose}
            disabled={!!busy}
            aria-label="Close"
            title="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '14px' }}>
          <div style={{ minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
              Date
            </label>
            <input
              type="date"
              value={dayKey}
              onChange={(e) => setDayKey(e.target.value)}
              className="add-calendar-event-date-input"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #2a2a2a',
                background: '#111',
                color: '#e0e0e0',
                outline: 'none',
              }}
              disabled={!!busy}
            />
          </div>

          <div style={{ minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #2a2a2a',
                background: '#111',
                color: '#e0e0e0',
                outline: 'none',
              }}
              disabled={!!busy}
            >
              <option value="workout">Workout</option>
              <option value="competition">Competition</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Hill repeats / Race day / Weekly mileage goal"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #2a2a2a',
              background: '#111',
              color: '#e0e0e0',
              outline: 'none',
            }}
            disabled={!!busy}
          />
        </div>

        <div style={{ marginTop: '12px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes…"
            rows={4}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #2a2a2a',
              background: '#111',
              color: '#e0e0e0',
              outline: 'none',
              resize: 'vertical',
            }}
            disabled={!!busy}
          />
        </div>

        {isCompetition ? (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
                  Race type
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={raceTypeId}
                    onChange={(e) => setRaceTypeId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #2a2a2a',
                      background: '#111',
                      color: '#e0e0e0',
                      outline: 'none',
                      flex: 1,
                    }}
                    disabled={!!busy}
                  >
                    <optgroup label="Presets">
                      {(raceTypeOptions?.publicList || []).map((rt) => (
                        <option key={rt.id} value={rt.id}>
                          {rt.name}
                        </option>
                      ))}
                    </optgroup>
                    {(raceTypeOptions?.userList || []).length > 0 ? (
                      <optgroup label="Your types">
                        {(raceTypeOptions?.userList || []).map((rt) => (
                          <option key={rt.id} value={rt.id}>
                            {rt.name}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </select>
                  {onManageRaceTypes ? (
                    <button type="button" style={actionBtnStyle} onClick={onManageRaceTypes} disabled={!!busy} title="Create or edit race types (duration, zone mix)">
                      Manage
                    </button>
                  ) : null}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
                  Priority
                </label>
                <select
                  value={eventPriority}
                  onChange={(e) => setEventPriority(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #2a2a2a',
                    background: '#111',
                    color: '#e0e0e0',
                    outline: 'none',
                  }}
                  disabled={!!busy}
                >
                  {EVENT_PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedRaceType?.defaults ? (
              <>
                <div style={{ marginTop: '10px', padding: '10px 12px', background: '#151515', borderRadius: '8px', border: '1px solid #2a2a2a' }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
                    Duration & zone mix (from race type — edit in Manage)
                  </div>
                  <div style={{ fontSize: '14px', color: '#e0e0e0' }}>
                    {durationMinutes ? `${durationMinutes} min` : '—'} · Z1 {zoneZ1}% · Z2 {zoneZ2}% · Z3 {zoneZ3}% · Z4 {zoneZ4}% · Z5 {zoneZ5}%
                  </div>
                </div>
              </>
            ) : (
              <div style={{ marginTop: '10px', padding: '10px 12px', background: '#151515', borderRadius: '8px', border: '1px solid #2a2a2a', fontSize: '13px', color: '#9aa0a6' }}>
                No default profile for this type. Use <strong>Manage</strong> to create a custom race type with duration and zone mix, then select it here.
              </div>
            )}
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            {mode === 'edit' && initialEvent?.id && onDelete ? (
              <button
                type="button"
                style={{ ...actionBtnStyle, borderColor: '#3a1b1b', color: '#ff8a8a' }}
                disabled={!!busy}
                onClick={async () => {
                  const ok = await onDelete?.(initialEvent.id);
                  if (ok !== false) {
                    onClose?.();
                  }
                }}
              >
                Delete Event
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" style={actionBtnStyle} onClick={onClose} disabled={!!busy}>
              Cancel
            </button>
            <button
              type="button"
              style={primaryBtnStyle}
              disabled={!!busy || !canSave}
              onClick={async () => {
                if (!canSave) return;
                const ok = await onSubmit?.({
                  id: initialEvent?.id || null,
                  dayKey,
                  type,
                  title: title.trim(),
                  notes: notes.trim() || '',
                  competition: isCompetition
                    ? {
                        raceTypeId,
                        raceTypeName: selectedRaceType?.name || null,
                        priority: eventPriority,
                        effortProfile,
                      }
                    : null,
                });
                if (ok !== false) {
                  setTitle('');
                  setNotes('');
                  onClose?.();
                }
              }}
            >
              {mode === 'edit' ? 'Save Changes' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

